import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";

export type TabLink = { to: string; label: string; end?: boolean; icon?: ReactNode; count?: number };

const tabClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "inline-flex min-h-11 items-center gap-2 whitespace-nowrap border-b-2 px-4 font-heading text-base font-medium no-underline transition-colors duration-fast",
    isActive ? "border-brand text-heading" : "border-transparent text-fg-muted hover:border-border-strong hover:text-heading",
  );

/**
 * Route tabs: each tab is a link, so the URL is the state (deep-linkable). The current tab gets
 * `aria-current="page"` from NavLink. Scrolls horizontally on small screens.
 */
export function NavTabs({ label, tabs, className }: { label: string; tabs: TabLink[]; className?: string }) {
  const { t } = useTranslation();
  return (
    <nav aria-label={label} className={cn("relative -mx-4 overflow-x-auto border-b border-border px-4 sm:mx-0 sm:px-0", className)}>
      <div className="flex gap-1">
        {tabs.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end={tab.end} className={tabClass}>
            {tab.icon}
            {tab.label}
            {tab.count ? (
              <>
                <span aria-hidden="true" className="rounded-pill bg-primary px-2 text-xs font-semibold text-primary-fg">
                  {tab.count}
                </span>
                <span className="sr-only">{t("common.waitingCount", { count: tab.count })}</span>
              </>
            ) : null}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
